import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

import { getEnvironmentLabel } from 'app/environment';
import tllBird from 'assets/icons/tll-bird.svg';
import { focusRing, reducedMotion } from 'ui/a11y';
import { typeStep } from 'ui/type';
import { VisuallyHidden } from 'ui/visually-hidden';

import { up } from './breakpoints';

const sharedCss = `
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  min-width: 0; /* let the lockup shrink below its content size instead of overflowing the bar */
  color: inherit;
  text-decoration: none;

  ${up('md')} {
    gap: 12px;
  }
`;

const LockupLink = styled(NavLink)`
  ${sharedCss}
  ${focusRing}
`;

const LockupStatic = styled.span`
  ${sharedCss}
`;

// Bird width is height * 1.04 (32 -> 33, 36 -> 37, 40 -> 42), per spec.
const Bird = styled.img`
  display: block;
  flex-shrink: 0; /* the name truncates before the bird would ever be squeezed */
  width: 33px;
  height: 32px;

  ${up('sm')} {
    width: 37px;
    height: 36px;
  }

  ${up('md')} {
    width: 42px;
    height: 40px;
  }
`;

// Sizes exactly to `Bird` (a single `display: block` img child), so `EnvPill` below can center
// itself on the bird at every breakpoint without repeating Bird's own xs/sm/md sizes here.
const BirdWrap = styled.span`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
`;

// Pulses the pill's own glow from nothing to a soft yellow halo and back, over a slow 10s cycle -
// `theme.colors.primary` (the CVI yellow) at zero blur/spread (i.e. invisible) to a soft blurred
// ring and back. A plain `box-shadow` (not `filter: drop-shadow`, which is more expensive to
// animate and can be clipped by some browsers' compositing in ways box-shadow isn't) - it paints
// outside the element's border box without affecting layout/scrollable overflow, so it can never
// itself be the cause of horizontal overflow at 320px (see `Lockup`'s own Playwright coverage).
// A factory taking the colour, because `keyframes` cannot evaluate prop/theme interpolations: a
// `${p => p.theme...}` inside it is pasted in as the function's source text, and the browser drops
// the invalid declarations, so the pulse silently never renders.
const glowPulse = (color: string) => keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 ${color};
  }
  50% {
    box-shadow: 0 0 8px 3px ${color};
  }
`;

/**
 * The non-production environment pill (TEST/DEV/...), shown as a diagonal sash across the TLL
 * bird — a deliberate product request, not a `src/ui` primitive. `aria-hidden` because its text
 * would otherwise fold into the enclosing NavLink's accessible name mid-word (e.g. "Lubade
 * infosüsteemTEST"); `Lockup` adds one `VisuallyHidden` announcement of the same fact instead, see
 * below.
 */
const EnvPill = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  /* Sized to its own text (not a fixed width) so a label anywhere from "DEV" up to
     getEnvironmentLabel's 8-character cap still reads as a deliberate sash rather than either
     wrapping or padding out to some arbitrary fixed box. TEST and DEV (the two values this project
     actually sets) render as a short word well inside the bird's own footprint at every
     breakpoint; STAGING (7 chars, the longest name in the closed isKnownNonProductionEnvironment
     list - app/environment.js - that isn't itself truncated by the cap) runs noticeably wider than
     the bird at xs, more comfortably at md, but the sash is still legible and doesn't wrap or
     clip - it just reads as a longer sash, same as a real diagonal ribbon would. */
  transform: translate(-50%, -50%) rotate(-45deg);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  padding: ${p => p.theme.space.quarter} ${p => p.theme.space.half};
  background: ${p => p.theme.colors.brownDark};
  color: ${p => p.theme.colors.pale};
  /* Fully rounded, not tokens.radiusChip (5px - "every LIS chip is 5px, not a pill") - explicit
     product request for a pill/sash look specifically here, the one deliberate exception. Do not
     use a literal backtick in this comment - it would close this styled-components template
     literal early (see the JSDoc comment above this component for the same note in prose). */
  border-radius: 999px;
  ${typeStep('xs')}
  /* Tighter than xs's own 1px letter-spacing default, so the label stays as small as possible -
     the design system's type floor (14px, see tokens.ts) already sets the lower bound on the text
     itself, this is the one dimension still free to tighten further. */
  letter-spacing: 0.4px;
  pointer-events: none;
  ${p => css`
    animation: ${glowPulse(p.theme.colors.primary)} 10s ease-in-out infinite;
  `}

  ${reducedMotion(css`
    animation: none;
    box-shadow: 0 0 8px 3px ${p => p.theme.colors.primary};
  `)}
`;

const Name = styled.span`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-family: ${p => p.theme.font.family};
  font-size: 16px;
  font-weight: ${p => p.theme.font.weight.bold};
  color: ${p => p.theme.colors.brown};

  ${up('sm')} {
    font-size: 17px;
  }

  ${up('md')} {
    font-size: 18px;
  }
`;

export interface LockupProps {
  /** Whether the signed-in user is allowed into the app (`isUserAllowed`) — gates the `/` link. */
  isLink: boolean;
}

/**
 * The TLL bird + "Lubade infosüsteem". An allowed user gets it as a link to `/`; everyone else
 * (signed out, or signed in but not allowed) gets the same markup without a link — showing the
 * logo in that case is a deliberate change from the old header, which hid it entirely.
 */
export function Lockup({ isLink }: LockupProps) {
  const { t } = useTranslation();
  const environmentLabel = getEnvironmentLabel();
  const content = (
    <>
      <BirdWrap>
        <Bird src={tllBird} alt="" />
        {environmentLabel && <EnvPill aria-hidden="true">{environmentLabel}</EnvPill>}
      </BirdWrap>
      <Name>{t('dashboard.appName')}</Name>
      {/* Announced once, alongside the pill's own aria-hidden text - a screen reader user gets
          "Lubade infosüsteem (TEST keskkond)" (or the `isLink` NavLink's equivalent), not a bare
          "TEST" mid-name. */}
      {environmentLabel && <VisuallyHidden>{t('header.environmentPillHint', { label: environmentLabel })}</VisuallyHidden>}
    </>
  );

  if (isLink) {
    return <LockupLink to="/">{content}</LockupLink>;
  }

  return <LockupStatic>{content}</LockupStatic>;
}

export default Lockup;
